import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, UserPlus, Loader2, Mail, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn, formatUserDisplayName } from '../../lib/utils';
import { secureApi } from '../../api/secure';
import { useUser } from '../../hooks/queries/useAuth';
import { useProjects } from '../../hooks/queries/useProjects';
import { getMaxCollaborators } from '../../types/subscriptions';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { useCollaborators, useInvitations } from '../../hooks/queries/useCollaborators';

type CollaboratorsModalProps = {
  projectId: string;
  projectName?: string;
  onClose: () => void;
};

const CollaboratorsModal: React.FC<CollaboratorsModalProps> = ({
  projectId,
  projectName: propProjectName,
  onClose,
}) => {
  // Use React Query hooks for collaborators and invitations
  const { data: collaborators = [], isLoading: isLoadingCollaborators } =
    useCollaborators(projectId);
  const { data: pendingInvitations = [], isLoading: isLoadingInvitations } =
    useInvitations(projectId);
  const isLoading = isLoadingCollaborators || isLoadingInvitations;
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [totalCollaboratorCount, setTotalCollaboratorCount] = useState<number>(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { data: user } = useUser();
  const { data: projects = [] } = useProjects();

  // Get project name from props or from the project store
  const projectName = propProjectName || projects.find(p => p.id === projectId)?.name || '';

  const fetchPermissions = useCallback(async () => {
    setError('');

    try {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get current user's session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Call the secure Supabase Edge Function to check collaborator permissions
      const { data, error } = await supabase.functions.invoke('check-collaborator-permissions', {
        body: JSON.stringify({ userId: user.id }),
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      // Log the response using the logger utility
      logger.fetch('Collaborator permissions response', {
        data,
        error,
        userId: user.id,
      });

      if (error) {
        logger.error('Failed to check collaborator permissions', error);
        throw error;
      }

      // The server explicitly tells us if the user is VIP and if they can invite collaborators
      logger.fetch('Collaboration permissions check', {
        currentTier: data?.currentTier,
        isVip: data?.isVip, // Use the explicit isVip property from the server
        canInvite: data?.canInviteCollaborators,
        collaboratorCount: data?.totalCollaboratorCount,
        maxCollaborators: data?.maxCollaborators,
      });

      // Set state based on server response
      setSubscriptionTier(data.currentTier);
      setTotalCollaboratorCount(data.totalCollaboratorCount);

      // The server has already calculated this correctly, accounting for VIP status
      setShowUpgradePrompt(!data.canInviteCollaborators);
    } catch (err: unknown) {
      setError('Failed to fetch collaborators and invitations.');
      console.error(err);
    }
  }, [projectId]);

  useEffect(() => {
    void fetchPermissions();
  }, [projectId, fetchPermissions]);

  const handleAddCollaborator = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting || showUpgradePrompt) return;

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      // The showUpgradePrompt state is already set by fetchData
      // which calls the secure edge function, so we can rely on it
      if (showUpgradePrompt) {
        setIsSubmitting(false);
        return;
      }

      // Check if the email is already invited or a collaborator
      const isAlreadyCollaborator = collaborators.some(
        c => c.users_with_details?.email.toLowerCase() === email.trim().toLowerCase()
      );

      const isAlreadyInvited = pendingInvitations.some(
        i => i.invitee_email.toLowerCase() === email.trim().toLowerCase()
      );

      if (isAlreadyCollaborator) {
        setError('This user is already a collaborator on this project.');
        setIsSubmitting(false);
        return;
      }

      if (isAlreadyInvited) {
        setError('This user has already been invited to this project.');
        setIsSubmitting(false);
        return;
      }

      if (!user?.id) {
        setError('You must be logged in to invite collaborators.');
        setIsSubmitting(false);
        return;
      }

      // Use the new invitation system via secure API with improved error handling
      try {
        const payload = {
          email: email.trim(),
          projectId,
          inviterId: user?.id,
          origin: window.location.origin,
        };

        console.log('Sending invitation with payload:', payload);

        const response = await secureApi.supabaseAdmin('inviteUserByEmail', payload);

        if (response.status === 'added') {
          // User already exists and was added as a collaborator
          setSuccessMessage(`${email} has been added as a collaborator.`);
        } else if (response.status === 'invited') {
          // Invitation email was sent
          setSuccessMessage(`Invitation email sent to ${email}`);
        } else if (response.status === 'invited_no_email') {
          // Invitation created but email couldn't be sent
          setSuccessMessage(
            `Invitation created for ${email}, but email delivery failed. They can still join using their account.`
          );
        } else {
          // Unexpected response status
          console.warn('Unexpected response status:', response);
          setError(`Server returned an unexpected response. Try again or check logs.`);
        }

        // Reset form
        setEmail('');
      } catch (apiError: unknown) {
        console.error('API error inviting user:', apiError);

        // Extract and display detailed error information
        let errorMessage = 'Failed to invite user. Please try again.';

        if (
          apiError &&
          typeof apiError === 'object' &&
          'response' in apiError &&
          apiError.response?.data?.error
        ) {
          const axiosError = apiError as {
            response: { data: { error: string; details?: string } };
          };
          errorMessage = `Error: ${axiosError.response.data.error}`;
          if (axiosError.response.data.details) {
            errorMessage += ` - ${axiosError.response.data.details}`;
          }
        } else if (apiError instanceof Error) {
          errorMessage = apiError.message;
        }

        setError(errorMessage);
      }
    } catch (outerErr: unknown) {
      console.error('Error in handleAddCollaborator:', outerErr);
      setError(
        outerErr instanceof Error
          ? outerErr.message
          : 'Failed to process invitation. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-labelledby="collaborators-modal-title"
        aria-modal="true"
        className="fixed inset-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <div className="bg-white w-full sm:w-[95vw] max-w-md sm:rounded-xl shadow-xl max-h-[100vh] sm:max-h-[95vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <h2
              id="collaborators-modal-title"
              className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2"
            >
              {projectName ? `${projectName} - Sharing` : 'Project Sharing'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close modal"
              disabled={isSubmitting}
            >
              <X size={20} className="sm:hidden" />
              <X size={24} className="hidden sm:block" />
            </button>
          </div>

          {/* Collaborators List */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg">
                {successMessage}
              </div>
            )}

            {/* Upgrade prompt */}
            {showUpgradePrompt && (
              <div className="bg-amber-50 border-2 border-amber-300 text-amber-900 p-4 rounded-lg shadow-md">
                <div className="flex items-start">
                  <AlertTriangle size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="font-semibold text-amber-800">Upgrade Required</h3>
                    <p className="text-sm mt-1 text-amber-700">
                      {subscriptionTier === 'PRO' ? (
                        <>
                          You need a team plan to invite collaborators. The Pro plan is for
                          individual use only.
                        </>
                      ) : subscriptionTier === 'SMALL_TEAM' ? (
                        <>
                          You've reached your Team Starter limit of{' '}
                          {getMaxCollaborators(subscriptionTier)} collaborators. Upgrade to Team
                          Growth for up to 20 team members.
                        </>
                      ) : subscriptionTier === 'MEDIUM_TEAM' ? (
                        <>
                          You've reached your Team Growth limit of{' '}
                          {getMaxCollaborators(subscriptionTier)} collaborators. Upgrade to Team
                          Gold for up to 50 team members.
                        </>
                      ) : (
                        <>
                          You've reached your collaborator limit ({totalCollaboratorCount} of{' '}
                          {getMaxCollaborators(subscriptionTier)}). Please contact support to
                          discuss enterprise options.
                        </>
                      )}
                    </p>
                    <div className="mt-3">
                      <a
                        href={`/subscription?reason=${subscriptionTier === 'PRO' ? 'project_sharing' : 'collaborator_limit'}&tier=${subscriptionTier}`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                      >
                        View Plans
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Collaborators</h3>
              {isLoading ? (
                <div className="flex items-center justify-center py-3 bg-gray-100 rounded-lg">
                  <Loader2 size={24} className="animate-spin text-blue-500" />
                </div>
              ) : collaborators.length === 0 ? (
                <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                  No collaborators yet
                </div>
              ) : (
                <ul className="space-y-2">
                  {collaborators.map(collab => (
                    <li
                      key={collab.id}
                      className="flex items-center justify-between bg-gray-100 p-3 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-500" />
                        <span>
                          {formatUserDisplayName(collab.users_with_details?.display_name) ||
                            collab.users_with_details?.email ||
                            'Unknown User'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500 capitalize">{collab.role}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Subscription tier info */}
            {!isLoading && !showUpgradePrompt && subscriptionTier && (
              <div className="text-xs text-gray-500 mt-2">
                <span className="font-medium">
                  {getMaxCollaborators(subscriptionTier) === 'unlimited'
                    ? 'Unlimited collaborators'
                    : `${totalCollaboratorCount} of ${getMaxCollaborators(subscriptionTier)} collaborators used`}
                </span>
                <span className="ml-1">
                  ({subscriptionTier.charAt(0) + subscriptionTier.slice(1).toLowerCase()} plan)
                </span>
              </div>
            )}

            {!isLoading && pendingInvitations.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-2">Pending Invitations</h3>
                <ul className="space-y-2">
                  {pendingInvitations.map(invitation => (
                    <li
                      key={invitation.id}
                      className="flex items-center justify-between bg-yellow-50 p-3 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-yellow-500" />
                        <span>{invitation.invitee_email}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                        <Mail size={14} />
                        <span>Invited</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Add Collaborator Form */}
          <form
            ref={formRef}
            onSubmit={handleAddCollaborator}
            className="border-t border-gray-200 p-4 sm:p-6 bg-white"
          >
            <div className="flex gap-2">
              <div className="flex-1">
                <label htmlFor="collaborator-email" className="sr-only">
                  Collaborator Email
                </label>
                <input
                  id="collaborator-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={
                    showUpgradePrompt
                      ? 'Upgrade to invite collaborators'
                      : 'Enter collaborator email'
                  }
                  className={cn(
                    'w-full border rounded-lg px-3 py-2 outline-none',
                    showUpgradePrompt
                      ? 'border-gray-200 bg-gray-100 text-gray-400'
                      : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                  )}
                  required
                  disabled={isSubmitting || showUpgradePrompt}
                />
              </div>
              <button
                type="submit"
                className={cn(
                  'px-4 py-2 rounded-lg',
                  'transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2',
                  showUpgradePrompt
                    ? 'bg-gray-300 text-gray-500'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                )}
                disabled={!email.trim() || isSubmitting || showUpgradePrompt}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : showUpgradePrompt ? (
                  <>
                    <UserPlus size={18} />
                    <span>Upgrade</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={18} />
                    <span>Add</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CollaboratorsModal;
